import React from "react";
import {
  faRulerVertical,
  faWeightScale,
  faHeartbeat
} from "@fortawesome/pro-solid-svg-icons";

import Box from "@/components/shared/Box";
import Avatar from "@/components/shared/Avatar";
import Progressbar from "@/components/shared/Progressbar";

import type { TecnicalSpecificationsProps } from "./technicalSpecifications.types";

import s from "./technicalSpecifications.module.scss";

const TecnicalSpecifications = ({
  tecnicalSpecifications
}: TecnicalSpecificationsProps) => {
  const {
    altura,
    peso,
    expectativaDeVida,
    classificacaoFIFE,
    gruposFCI,
    tamanho,
    pelagem,
    cores,
    caracteristicasComportamentais
  } = tecnicalSpecifications;

  const formatPelagem = (pelagem: string) => {
    switch (pelagem) {
      case "Pelo médio":
        return "Médio";
      case "Pelo curto":
        return "Curto";
      case "Pelo longo":
        return "Longo";
      default:
        return pelagem;
    }
  };

  const headSpecifications = [
    {
      label: "Altura",
      value: altura,
      icon: faRulerVertical,
      color: "var(--token-cobasi-colors-turquoise-primary)"
    },
    {
      label: "Peso",
      value: peso,
      icon: faWeightScale,
      color: "var(--token-cobasi-colors-orange-primary)"
    },
    {
      label: "Expectativa de vida",
      value: expectativaDeVida,
      icon: faHeartbeat,
      color: "var(--token-cobasi-colors-blue-bikini-primary)"
    }
  ];

  const bodySpecifications = [
    {
      label: `${classificacaoFIFE ? "Classificação FIFE" : "Grupos FCI"}`,
      value: classificacaoFIFE || gruposFCI
    },
    {
      label: "Tamanho",
      value: tamanho
    },
    {
      label: "Pelo",
      value: formatPelagem(pelagem)
    },
    {
      label: "Cores",
      value: cores
    }
  ];

  return (
    <div className={s.wrapper}>
      <Box
        padding={"var(--token-spacings-xxs)"}
        border={"1px solid var(--token-colors-neutral-cloud-normal)"}
        borderRadius={"var(--token-border-radius-lg)"}
        gap={"var(--token-spacings-xxs)"}
      >
        <div className={s.head}>
          {headSpecifications.map(({ label, value, icon, color }, index) => (
            <div
              className={`${s["head_item"]} ${index === 1 ? s["head_item_middle"] : ""}`}
              key={label}
            >
              <Avatar
                color={color}
                icon={icon}
                pill="top-start"
                iconProps={{ color: "var(--token-colors-neutral-cloud-white)" }}
              />
              <div className={s["head_details"]}>
                <span className="subtitle-sm" title={label}>
                  {label}
                </span>
                <span
                  className="body-text-sm truncate"
                  title={value}
                  style={{ maxWidth: "8rem" }}
                >
                  {value}
                </span>
              </div>
            </div>
          ))}
        </div>
        <hr className="divider" />
        <div className={s.body}>
          {bodySpecifications.map(({ label, value }) => (
            <div className={s["body_details"]} key={label}>
              <span className="subtitle-sm" title={label}>
                {label}
              </span>
              <span
                className={`body-text-sm ${s["body_details_text"]}`}
                title={value}
              >
                {value}
              </span>
            </div>
          ))}
        </div>
      </Box>
      <div className={s.characteristics}>
        <h2 className="heading-xs">Características</h2>
        <Box
          padding={"var(--token-spacings-xxs)"}
          border={"1px solid var(--token-colors-neutral-cloud-normal)"}
          borderRadius={"var(--token-border-radius-lg)"}
        >
          <div className={s["characteristics_details"]}>
            {caracteristicasComportamentais.map(
              ({ caracteristica, progresso, id }) => (
                <div className={s["characteristics_item"]} key={id}>
                  <span className="subtitle-sm" title={caracteristica}>
                    {caracteristica}
                  </span>
                  <Progressbar
                    progress={progresso}
                    width="38%"
                    color="var(--token-cobasi-colors-orange-primary)"
                  />
                </div>
              )
            )}
          </div>
        </Box>
      </div>
    </div>
  );
};

export default TecnicalSpecifications;
